import {
  normalizePartnerCode,
  PARTNER_CODE_MAX_LENGTH,
  PARTNER_NAME_MAX_LENGTH,
  PARTNER_NOTE_MAX_LENGTH,
  type Partner,
  type PartnerKind,
} from '@pulsify/shared/dto'
import { type FormEvent, type ReactNode, useId, useState } from 'react'
import { useCreatePartner, useUpdatePartner } from '~/entities/partner/model/use-partners'
import * as m from '~/paraglide/messages'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/shared/ui/dialog'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Textarea } from '~/shared/ui/textarea'

interface PartnerFields {
  name: string
  kind: PartnerKind
  code: string
  contactEmail: string
  note: string
}

function initialFields(partner?: Partner): PartnerFields {
  return {
    name: partner?.name ?? '',
    kind: partner?.kind ?? 'person',
    code: partner?.code ?? '',
    contactEmail: partner?.contactEmail ?? '',
    note: partner?.note ?? '',
  }
}

function PartnerForm({ partner, onDone }: { partner?: Partner; onDone: () => void }) {
  const fieldId = useId()
  const [fields, setFields] = useState(() => initialFields(partner))
  const create = useCreatePartner()
  const update = useUpdatePartner(partner?.id ?? '')
  const pending = create.isPending || update.isPending
  const set = <K extends keyof PartnerFields>(key: K, value: PartnerFields[K]) =>
    setFields((current) => ({ ...current, [key]: value }))

  const codeInvalid = !partner && fields.code.trim() !== '' && !normalizePartnerCode(fields.code)
  const canSubmit = fields.name.trim() !== '' && !codeInvalid && !pending

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const body = {
      name: fields.name.trim(),
      kind: fields.kind,
      contactEmail: fields.contactEmail.trim() || null,
      note: fields.note.trim() || null,
    }
    if (partner) {
      update.mutate(body, { onSuccess: onDone })
    } else {
      const code = normalizePartnerCode(fields.code) ?? undefined
      create.mutate({ ...body, code }, { onSuccess: onDone })
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`${fieldId}-name`}>{m.partnersNameLabel()}</Label>
        <Input
          id={`${fieldId}-name`}
          value={fields.name}
          onChange={(event) => set('name', event.target.value)}
          maxLength={PARTNER_NAME_MAX_LENGTH}
          placeholder={m.partnersNamePlaceholder()}
          autoFocus
        />
      </div>
      <div className="space-y-1.5">
        <Label>{m.partnersKindLabel()}</Label>
        <div>
          <SegmentedControl
            label={m.partnersKindLabel()}
            value={fields.kind}
            onChange={(kind) => set('kind', kind)}
            options={[
              { value: 'person', label: m.partnersKindPerson() },
              { value: 'organisation', label: m.partnersKindOrganisation() },
            ]}
          />
        </div>
      </div>
      {partner ? null : (
        <div className="space-y-1.5">
          <Label htmlFor={`${fieldId}-code`}>{m.partnersCodeLabel()}</Label>
          <Input
            id={`${fieldId}-code`}
            value={fields.code}
            onChange={(event) => set('code', event.target.value)}
            maxLength={PARTNER_CODE_MAX_LENGTH}
            aria-invalid={codeInvalid}
            className="font-mono"
          />
          <p className="text-muted-foreground text-xs">{m.partnersCodeHint()}</p>
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor={`${fieldId}-email`}>{m.partnersEmailLabel()}</Label>
        <Input
          id={`${fieldId}-email`}
          type="email"
          value={fields.contactEmail}
          onChange={(event) => set('contactEmail', event.target.value)}
          maxLength={254}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${fieldId}-note`}>{m.partnersNoteLabel()}</Label>
        <Textarea
          id={`${fieldId}-note`}
          value={fields.note}
          onChange={(event) => set('note', event.target.value)}
          maxLength={PARTNER_NOTE_MAX_LENGTH}
          placeholder={m.partnersNotePlaceholder()}
          rows={3}
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onDone}>
          {m.cancel()}
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {partner ? m.save() : m.partnersCreate()}
        </Button>
      </DialogFooter>
    </form>
  )
}

/** Opens a form to add a partner, or to edit `partner` when given. */
export function PartnerFormDialog({ partner, trigger }: { partner?: Partner; trigger: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{partner ? m.partnersEditTitle() : m.partnersCreate()}</DialogTitle>
        </DialogHeader>
        {open ? <PartnerForm partner={partner} onDone={() => setOpen(false)} /> : null}
      </DialogContent>
    </Dialog>
  )
}
